import {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useForm } from "antd/es/form/Form";

import { GenericDataFormContext } from "../../../../shared/contexts/GenericDataForm";
import { MasterContext } from "../../../../../../../shared/contexts";
import { StepOneFormContext } from "./StepOneFormContext";

import { DebtQuote } from "../../../../shared/interfaces/services/get-debt-columns-service.interface";
import {
  GetSummaryQuotesServiceResponse,
  LOADING_SAVED_PRELIQUIDATIONS_STATES,
  PreLiquidationStep,
} from "../../../../shared/interfaces";
import { SavedPreliquidation } from "../../interfaces";

import PreLiquidationService from "../../../../../../../shared/services/collection/preliquidation";
import {
  applyCheckboxChangeToRow,
  getDebtQuoteKey,
  rowHasCheckboxSelection,
} from "../../../../shared/utils/debt-quote-checkbox";

const {
  INIT_LOADING,
  STEP_ONE_DATA_FILLED,
  STEP_ONE_DATA_FILLING,
  STEP_ONE_SERVICES_LOADING,
  STEP_ONE_SERVICES_LOADED,
  HAS_ERROR,
} = LOADING_SAVED_PRELIQUIDATIONS_STATES;

interface QuotaInterface {
  articulo: string;
  superPoliza: string;
  suplementoSupPol: string;
  rama: string;
  arse: string;
  operacion: string;
  suplemento: string;
  poliza: string;
  numero: string;
  subCuota: string;
  vencimiento: string;
  importePesos: number;
  comision: string;
  codMoneda: string;
  nombreMoneda: string;
  abreviaturaMoneda: string;
  importeMonedaExtranjera: string;
  asegurado: string;
}

interface QuotasData {
  cuotas: QuotaInterface[];
}

export const StepOneFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const [filterForm] = useForm();
  const [wasPreliquidationFetched, setWasPreliquidationFetched] =
    useState<boolean>(false);
  const [preliquidationData, setPreliquidationData] = useState<
    Array<DebtQuote>
  >([]);
  const [preliquidationDataWithFilters, setPreliquidationDataWithFilters] =
    useState<Array<DebtQuote>>([]);
  const [savedPreliquidationData, setSavedPreliquidationData] =
    useState<SavedPreliquidation>({
      fullCheckedColumns: [],
      individualCheckedCells: [],
    });
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);

  // Ref para mantener un registro persistente de todos los estados de checkboxes
  interface CheckboxStates {
    deudaAnteriorOPT?: boolean;
    quincenaAnteriorOPT?: boolean;
    quincenaActualOPT?: boolean;
    quincenaSiguienteOPT?: boolean;
    quincenaPosteriorOPT?: boolean;
    saldoOPT?: boolean;
    pagoContadoOPT?: boolean;
  }
  const checkboxStatesRef = useRef<Map<string, CheckboxStates>>(new Map());

  const {
    currentStep,
    preliquidationInfo,
    setCanShowSpinner,
    selectedQuotas,
    loadPreliquidationState,
    setLoadPreliquidationState,
    getListOfSelectedQuotas,
    loadingSavedPreliquidation,
    preliquidationTotalValue
  } = useContext(GenericDataFormContext);
  const { branchList } = useContext(MasterContext);

  const checkboxList = useMemo(
    () => [
      "deudaAnteriorOPT",
      "quincenaAnteriorOPT",
      "quincenaActualOPT",
      "quincenaSiguienteOPT",
      "quincenaPosteriorOPT",
      "saldoOPT",
      "pagoContadoOPT",
      /* eslint-disable-next-line */
    ],
    []
  );

  const syncCheckboxStateRef = useCallback((row: DebtQuote) => {
    checkboxStatesRef.current.set(getDebtQuoteKey(row), {
      deudaAnteriorOPT: row.deudaAnteriorOPT,
      quincenaAnteriorOPT: row.quincenaAnteriorOPT,
      quincenaActualOPT: row.quincenaActualOPT,
      quincenaSiguienteOPT: row.quincenaSiguienteOPT,
      quincenaPosteriorOPT: row.quincenaPosteriorOPT,
      saldoOPT: row.saldoOPT,
      pagoContadoOPT: row.pagoContadoOPT,
    });
  }, []);

  const totalSelected = useMemo(() => {
    if (parseFloat(preliquidationTotalValue) > 0) {
      return parseFloat(preliquidationTotalValue);
    }

    let total = 0;

    preliquidationData.forEach((row) => {
      if (!rowHasCheckboxSelection(row)) return;

      if ((row as any).pagoContadoOPT) {
        const raw = (row as any).pagoContado;
        total += parseFloat(raw === ".00" ? "0" : raw);
        return;
      }

      checkboxList.forEach((checkbox) => {
        if (checkbox === "pagoContadoOPT") return;

        if ((row as any)[checkbox]) {
          const checkboxValue = checkbox.substring(0, checkbox.length - 3);
          const raw = (row as any)[checkboxValue];
          total += parseFloat(raw === ".00" ? "0" : raw);
        }
      });
    });

    return total;
  }, [checkboxList, preliquidationData, preliquidationTotalValue]);

  const hasSelections = useCallback(
    () => preliquidationData.some((row) => rowHasCheckboxSelection(row)),
    [preliquidationData]
  );

  const applyMasterDataUpdates = useCallback(
    (updatesByKey: Map<string, DebtQuote>) => {
      if (!updatesByKey.size) return;

      setPreliquidationData((prev) =>
        prev.map((row) => updatesByKey.get(getDebtQuoteKey(row)) ?? row)
      );
      setPreliquidationDataWithFilters((prev) =>
        prev.map((row) => updatesByKey.get(getDebtQuoteKey(row)) ?? row)
      );

      updatesByKey.forEach((row) => syncCheckboxStateRef(row));
    },
    [syncCheckboxStateRef]
  );

  const updateRowCheckboxState = useCallback(
    (recordKey: string, column: string, checked: boolean) => {
      const updatesByKey = new Map<string, DebtQuote>();

      preliquidationData.forEach((item) => {
        if (getDebtQuoteKey(item) !== recordKey) return;
        const updated = applyCheckboxChangeToRow(
          item,
          column,
          checked,
          checkboxList
        );
        updatesByKey.set(recordKey, updated);
      });

      applyMasterDataUpdates(updatesByKey);
    },
    [applyMasterDataUpdates, checkboxList, preliquidationData]
  );

  const updateColumnCheckboxStateForVisible = useCallback(
    (column: string, checked: boolean) => {
      const updatesByKey = new Map<string, DebtQuote>();

      preliquidationDataWithFilters.forEach((item) => {
        const updated = applyCheckboxChangeToRow(
          item,
          column,
          checked,
          checkboxList
        );
        updatesByKey.set(getDebtQuoteKey(updated), updated);
      });

      applyMasterDataUpdates(updatesByKey);
    },
    [applyMasterDataUpdates, checkboxList, preliquidationDataWithFilters]
  );

  const filterPreliquidationData = useCallback(() => {
    const checkedColumnsValue = filterForm.getFieldValue("checkedColumns");
    const showOnlySelected = checkedColumnsValue === "true";
    const rama = filterForm.getFieldValue("rama");
    const asegurado = filterForm.getFieldValue("asegurado");
    const poliza = filterForm.getFieldValue("poliza");

    const filteredData = preliquidationData
      .filter(
        (el) =>
          (rama === undefined || el.rama == rama) &&
          (asegurado === undefined ||
            el.asegurado
              .toUpperCase()
              .indexOf(String(asegurado).toUpperCase()) !== -1) &&
          (poliza === undefined || el.poliza.indexOf(poliza) !== -1)
      )
      .filter((el) => (showOnlySelected ? rowHasCheckboxSelection(el) : true));

    setPreliquidationDataWithFilters(filteredData);
  }, [preliquidationData, filterForm]);

  const clearPreliquidationData = useCallback((): DebtQuote[] => {
    filterForm.resetFields();
    filterForm.setFieldsValue({ checkedColumns: "false" });
    setPreliquidationDataWithFilters(preliquidationData);
    return preliquidationData;
  }, [filterForm, preliquidationData]);

  const fetchPreliquidationData = useCallback(async () => {
    try {
      setCanShowSpinner(true);

      if (loadPreliquidationState) {
        setLoadPreliquidationState(STEP_ONE_SERVICES_LOADING);
        await getListOfSelectedQuotas();
      }

      const response = await PreLiquidationService.getDebtColumns(
        preliquidationInfo!.numeroPreliquidacion
      );

      response.data.cuotas = response.data.cuotas
        .map((item) => ({
          ...item,
          key: getDebtQuoteKey(item),
          ramaDescripcion: branchList?.find((el) => el.value === item.rama)
            ?.label,
        }));

      setPreliquidationData(response.data.cuotas);
      setPreliquidationDataWithFilters(response.data.cuotas);
      checkboxStatesRef.current.clear();
      response.data.cuotas.forEach((row) => syncCheckboxStateRef(row));
      setWasPreliquidationFetched(true);

      if (loadPreliquidationState) {
        setLoadPreliquidationState(STEP_ONE_SERVICES_LOADED);
      }
    } catch (error) {
      if (loadPreliquidationState) {
        setLoadPreliquidationState(HAS_ERROR);
      }
    } finally {
      setCanShowSpinner(false);
    }
  }, [
    preliquidationInfo,
    setPreliquidationData,
    setPreliquidationDataWithFilters,
    setWasPreliquidationFetched,
    loadPreliquidationState,
    setLoadPreliquidationState,
    getListOfSelectedQuotas,
    setCanShowSpinner,
    syncCheckboxStateRef,
  ]);

  const mergeQuotas = useCallback(
    (data?: GetSummaryQuotesServiceResponse): QuotasData => {
      if (!data) return { cuotas: [] };

      const merged: Record<string, QuotaInterface> = {};

      data.cuotas.forEach((cuota) => {
        const key = `${cuota.superPoliza}-${cuota.suplemento}`;

        if (!merged[key]) {
          merged[key] = { ...cuota, importePesos: Number(cuota.importePesos) };
        } else {
          merged[key].importePesos += Number(cuota.importePesos);
        }
      });

      return { cuotas: Object.values(merged) };
    },
    []
  );

  const fillStepOne = useCallback(async () => {
    try {
      setCanShowSpinner(true);
      setLoadPreliquidationState(STEP_ONE_DATA_FILLING);

      const quoteMap = new Map(
        preliquidationData.map((quota) => [
          `${quota.superpoliza}-${quota.suplemento}-${quota.asegurado}`,
          quota,
        ])
      );

      const quotas = mergeQuotas(selectedQuotas);

      const anuladasList: DebtQuote[] = [];
      preliquidationData.forEach((q) => {
        if (q.suplemento === "Poliza anulada") {
          anuladasList.push(q);
        }
      });

      //Primero obtener todas las cuotas de las anuladas y sacarlas de quotas?.cuotas
      //Luego procesar esas cuotas y setear un unico valor por esas cuotas en quoteMap
      //Luego seguir con el resto normalmente

      quotas?.cuotas.forEach((quotaResumen) => {
        anuladasList.forEach((cancelledQuota) => {
          if (cancelledQuota.superpoliza == quotaResumen.superPoliza) {
            const key = `${quotaResumen.superPoliza}-Poliza anulada-${quotaResumen.asegurado}`;
            quoteMap.set(key, {
              ...cancelledQuota,
              deudaAnteriorOPT: true,
              quincenaAnteriorOPT:true, 
              quincenaActualOPT: true,
              quincenaSiguienteOPT: true,
              quincenaPosteriorOPT: true,
              saldoOPT: true,
              pagoContadoOPT: false,
            });
          }
        });
      });
      
      quotas?.cuotas.forEach((selected) => {
        const key = `${selected.superPoliza}-${selected.suplemento}-${selected.asegurado}`;        
        
        let quota = quoteMap.get(key);

        if (quota) {
          const deudaAnterior =
            quota.deudaAnterior === quota.quincenaAnterior
              ? Number(quota.deudaAnterior)
              : Number(quota.deudaAnterior) + Number(quota.quincenaAnterior);

          const calculoDeudaAnterior =
            Number(selected.importePesos) - Number(quota.deudaAnterior);
          const calculoQuincenaAnterior =
            Number(selected.importePesos) - deudaAnterior;
          const calculoQuincenaActual =
            calculoQuincenaAnterior - Number(quota.quincenaActual);
          const calculoQuincenaSiguiente =
            calculoQuincenaActual - Number(quota.quincenaSiguiente);
          const calculoQuincenaPosterior =
            calculoQuincenaSiguiente - Number(quota.quincenaPosterior);
          const calculoSaldo = calculoQuincenaPosterior - Number(quota.saldo);
          const calculoPagoContado = calculoSaldo - Number(quota.pagoContado);

          quoteMap.set(key, {
            ...quota,
            deudaAnteriorOPT: calculoDeudaAnterior >= 0, //Si tiene la primer columnas, debería tener la segunda seleccionada
            quincenaAnteriorOPT: calculoQuincenaAnterior >= 0, //Si tiene la primer columnas, debería tener la segunda seleccionada
            quincenaActualOPT:
              calculoQuincenaActual > 0 ||
              (calculoQuincenaActual === 0 && Number(quota.quincenaActual) > 0),
            quincenaSiguienteOPT:
              calculoQuincenaSiguiente > 0 ||
              (calculoQuincenaSiguiente === 0 &&
                Number(quota.quincenaSiguiente) > 0),
            quincenaPosteriorOPT:
              calculoQuincenaPosterior > 0 ||
              (calculoQuincenaPosterior === 0 &&
                Number(quota.quincenaPosterior) > 0),
            saldoOPT:
              calculoSaldo > 0 ||
              (calculoSaldo === 0 && Number(quota.saldo) > 0),
            pagoContadoOPT:
              calculoPagoContado > 0 ||
              (calculoPagoContado === 0 && Number(quota.pagoContado) > 0),
          });
        }
      });

      const updatedMasterData = preliquidationData.map((row) => {
        const mapKey = `${row.superpoliza}-${row.suplemento}-${row.asegurado}`;
        const updated = quoteMap.get(mapKey);
        return updated ? { ...row, ...updated, key: getDebtQuoteKey(row) } : row;
      });

      setPreliquidationData(updatedMasterData);
      setPreliquidationDataWithFilters(updatedMasterData);
      updatedMasterData.forEach((row) => syncCheckboxStateRef(row));

      const preliquidationDataWithFilters = updatedMasterData;

      const savedPreliquidationData: SavedPreliquidation = {
        fullCheckedColumns: [],
        individualCheckedCells: [],
      };

      checkboxList.forEach((checkbox) => {
        if (
          preliquidationDataWithFilters.filter(
            (item) => (item as any)[checkbox] === true
          ).length === preliquidationDataWithFilters.length
        ) {
          savedPreliquidationData.fullCheckedColumns.push(checkbox);
        }
      });

      const partialCheckedColumns: Array<string> = checkboxList.filter(
        (col) => !savedPreliquidationData.fullCheckedColumns.includes(col)
      );

      preliquidationDataWithFilters.forEach((row) => {
        partialCheckedColumns.forEach((checkbox) => {
          if ((row as any)[checkbox] === true) {
            savedPreliquidationData.individualCheckedCells.push({
              key: row.key,
              colName: checkbox,
              articulo: row.articulo,
              superpoliza: row.superpoliza,
              suplemento: row.suplemento,
            });
          }
        });
      });
      
      setSavedPreliquidationData(savedPreliquidationData);

      setLoadPreliquidationState(STEP_ONE_DATA_FILLED);
    } catch (error) {
      setLoadPreliquidationState(HAS_ERROR);
    } finally {
      setCanShowSpinner(false);
    }
  }, [
    preliquidationInfo,
    setPreliquidationData,
    setPreliquidationDataWithFilters,
    setSavedPreliquidationData,
    checkboxList,
    mergeQuotas,
    selectedQuotas,
    setLoadPreliquidationState,
    preliquidationData,
    setCanShowSpinner,
    syncCheckboxStateRef,
  ]);

  useEffect(() => {
    if (
      currentStep === PreLiquidationStep.ONE &&
      !wasPreliquidationFetched &&
      !loadingSavedPreliquidation
    ) {
      fetchPreliquidationData();
    }
  }, [currentStep, wasPreliquidationFetched, loadingSavedPreliquidation]);

  useEffect(() => {
    if (
      loadPreliquidationState === INIT_LOADING &&
      preliquidationInfo &&
      !wasPreliquidationFetched
    ) {
      fetchPreliquidationData();
    }
  }, [loadPreliquidationState, preliquidationInfo, wasPreliquidationFetched]);

  useEffect(() => {
    if (
      loadPreliquidationState === STEP_ONE_SERVICES_LOADED &&
      preliquidationData
    ) {
      fillStepOne();
    }
  }, [loadPreliquidationState, preliquidationData]);

  const values = useMemo(
    () => ({
      filterForm,
      checkboxList,
      totalSelected,
      preliquidationData,
      setPreliquidationData,
      preliquidationDataWithFilters,
      setPreliquidationDataWithFilters,
      savedPreliquidationData,
      setSavedPreliquidationData,
      showConfirmationModal,
      setShowConfirmationModal,
      hasSelections,
      filterPreliquidationData,
      clearPreliquidationData,
      updateRowCheckboxState,
      updateColumnCheckboxStateForVisible,
    }),
    [
      filterForm,
      checkboxList,
      totalSelected,
      preliquidationData,
      setPreliquidationData,
      preliquidationDataWithFilters,
      setPreliquidationDataWithFilters,
      savedPreliquidationData,
      setSavedPreliquidationData,
      showConfirmationModal,
      setShowConfirmationModal,
      hasSelections,
      filterPreliquidationData,
      clearPreliquidationData,
      updateRowCheckboxState,
      updateColumnCheckboxStateForVisible,
    ]
  );

  return (
    <StepOneFormContext.Provider value={values}>
      {children}
    </StepOneFormContext.Provider>
  );
};
